// Types
import { ContactType, TargetType, UserRole } from 'api/graphql-global-types';
import {
  GetMediaPost_getMediaPost_ImagePost_mentions,
  GetMediaPost_getMediaPost_VideoPost_mentions,
} from 'api/mediaPost/types/GetMediaPost';
import { GetStream_stream_mentions } from 'api/streams/types/GetStream';
import { GetArticleBySlug_getArticleBySlug_mentions } from 'api/articles/types/GetArticleBySlug';
import { GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_mentions } from 'api/mediaPost/types/GetVideoEditRequestsForStore';
// Helpers
import { getUserRoleLabel } from 'helpers/user';

export type MentionData =
  | GetMediaPost_getMediaPost_ImagePost_mentions
  | GetMediaPost_getMediaPost_VideoPost_mentions
  | GetStream_stream_mentions
  | GetArticleBySlug_getArticleBySlug_mentions
  | GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_mentions;

export const formattedUnregisteredMention = (mention, type) => ({
  label:
    type === ContactType.ATHLETE
      ? mention?.unregisteredContact?.name || ''
      : mention?.unregisteredContact?.representativeName || '',
  value: mention?.unregisteredContact?.id || '',
  avatarURL: '',
  email: mention?.email || '',
  url: mention?.url || '',
  socialMediaLink: mention?.socialMediaLink || '',
});

export const reformattedUnregisteredMention = (option, type) => ({
  id: option.value,
  store: null,
  mentionType: 'MEDIA_POST',
  targetType: TargetType.UNREGISTERED,
  unregisteredContact: {
    id: option.value,
    representativeName: type === ContactType.ATHLETE ? null : option.label,
    name: type === ContactType.BRAND ? null : option.label,
    type: type,
    email: option.email,
    url: option.url,
    socialMediaLink: option?.socialMediaLink || '',
  },
});

export const formatMentionsInput = (mentions) => {
  return mentions.map((mention) => {
    if (mention.targetType === TargetType.REGISTERED) {
      return {
        storeId: mention?.store?.storeDetails?.storeId,
      };
    } else if (mention.targetType === TargetType.UNREGISTERED) {
      return {
        unregisteredContactId: mention?.unregisteredContact?.id,
      };
    }
  });
};

export const isMentionBrand = (mention: MentionData): boolean => {
  return (
    mention.unregisteredContact?.type === ContactType.BRAND ||
    (mention.targetType === TargetType.REGISTERED &&
      (mention.store as any)?.role === UserRole.Organization)
  );
};

export const getMentionLabel = (mention: MentionData): string => {
  const unregisteredContact = mention?.unregisteredContact;

  if (unregisteredContact) {
    return (
      unregisteredContact.name || unregisteredContact.representativeName || ''
    );
  }

  const store = mention?.store;
  if (!store) {
    return '';
  }
  const fromStoreDetails = store.storeDetails?.storeName;
  const fromPersonalName = `${store.firstName} ${store.lastName}`.trim();
  return fromStoreDetails || fromPersonalName || '';
};

export const getMentionAvatarUrl = (mention: MentionData): string => {
  const isBrand = isMentionBrand(mention);

  if (mention.targetType === TargetType.REGISTERED) {
    return (
      mention.store?.storeDetails?.avatarURL ||
      (isBrand
        ? '/images/iconsV3/BrandAvatarPlaceholder.svg'
        : '/images/icons/avatar-placeholder.svg')
    );
  }
  return isBrand
    ? '/images/iconsV3/BrandAvatarPlaceholder.svg'
    : '/images/icons/avatar-placeholder.svg';
};

export const getMentionTitle = (mention: MentionData): string => {
  if (mention.targetType === TargetType.REGISTERED) {
    const store = mention.store as any;
    const role = store?.role as UserRole;
    if (role) {
      return getUserRoleLabel(role);
    }
  }

  if (mention.unregisteredContact?.type === ContactType.BRAND) {
    return 'Brand not on MILLIONS';
  }

  return 'User not on MILLIONS';
};

// Sort mentions by registered status: registered first, then unregistered
export const sortByRegistered = (a: MentionData, b: MentionData) => {
  const aIsRegistered = a.targetType === TargetType.REGISTERED;
  const bIsRegistered = b.targetType === TargetType.REGISTERED;

  if (aIsRegistered && !bIsRegistered) return -1;
  if (!aIsRegistered && bIsRegistered) return 1;
  return 0;
};
